import { useMemo, useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { Calendar, Globe2, Mail, FileText, Loader2, AlertCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { Deal } from '@/hooks/use-deals';
import { useRecentDealByDomain } from '@/hooks/use-deals';
import { useRelatedDomains } from '@/hooks/use-related-domains';

interface RelatedDomainsSectionProps {
  deal: Deal;
  /** Domains already in the current deal to exclude from results */
  currentDomains: string[];
}

export function RelatedDomainsSection({ deal, currentDomains }: RelatedDomainsSectionProps) {
  const emailDomains = useMemo(() => {
    const domains = new Set<string>();

    if (deal.registrants?.people) {
      for (const person of deal.registrants.people) {
        if (person.email) {
          const domain = person.email.split('@')[1]?.toLowerCase();
          if (domain)
            domains.add(domain);
        }
      }
    }

    return [...domains];
  }, [deal]);

  // Extract regulatory licenses from deal registrants
  const regulatoryLicenses = useMemo(() => {
    const licenses = new Set<string>();

    // Add licenses from registrant people
    if (deal.registrants?.people) {
      for (const person of deal.registrants.people) {
        if (person.organization_licenses) {
          for (const license of person.organization_licenses) {
            // Format: "authority,value" for the API
            licenses.add(`${license.authority},${license.value}`);
          }
        }
      }
    }

    return [...licenses];
  }, [deal]);

  const hasSearchParams = emailDomains.length > 0 || regulatoryLicenses.length > 0;

  const { data: relatedDomains, isLoading, error } = useRelatedDomains(
    {
      emailDomains: emailDomains.length > 0 ? emailDomains : undefined,
      licenses: regulatoryLicenses.length > 0 ? regulatoryLicenses : undefined,
    },
    hasSearchParams,
  );

  // Filter out domains that are already in the current deal
  const filteredDomains = useMemo(() => {
    if (!relatedDomains)
      return [];
    const currentDomainsSet = new Set(currentDomains.map((d) => d.toLowerCase()));
    return relatedDomains.filter((domain) => !currentDomainsSet.has(domain.name.toLowerCase()));
  }, [relatedDomains, currentDomains]);

  const { getRecentDeal } = useRecentDealByDomain();
  const [dealLoading, setDealLoading] = useState<Record<string, boolean>>({});
  const PAGE_SIZE = 3;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredDomains.length]);

  const handleViewRecentDeal = useCallback(async (domain: string) => {
    setDealLoading((prev) => ({ ...prev, [domain]: true }));
    try {
      const recentDeal = await getRecentDeal(domain);
      if (recentDeal?.id) {
        window.open(`/app/deals/${recentDeal.id}`, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('No recent deal found for this domain');
      }
    } catch {
      toast.error('Failed to open recent deal');
    } finally {
      setDealLoading((prev) => ({ ...prev, [domain]: false }));
    }
  }, [getRecentDeal]);

  // Don't render anything if there's nothing to search for. This must stay
  // below every hook call so the hook order is stable across renders.
  if (!hasSearchParams) {
    return null;
  }

  const totalPages = Math.max(1, Math.ceil(filteredDomains.length / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedDomains = filteredDomains.slice(startIndex, startIndex + PAGE_SIZE);

  return (
    <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
      <div className="flex items-center gap-2">
        <Globe2 className="size-4 text-muted-foreground" />
        <span className="text-sm font-medium">Related Domains</span>
        {emailDomains.length > 0 && (
          <Badge variant="outline" className="gap-1 text-xs">
            <Mail className="size-3" />
            {emailDomains.length}
            {' '}
            email domain
            {emailDomains.length !== 1 && 's'}
          </Badge>
        )}
        {regulatoryLicenses.length > 0 && (
          <Badge variant="outline" className="gap-1 text-xs">
            <FileText className="size-3" />
            {regulatoryLicenses.length}
            {' '}
            license
            {regulatoryLicenses.length !== 1 && 's'}
          </Badge>
        )}
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Searching for related domains...
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 py-3 text-sm text-destructive">
          <AlertCircle className="size-4" />
          Failed to load related domains
        </div>
      )}

      {!isLoading && !error && filteredDomains.length === 0 && (
        <div className="py-3 text-sm text-muted-foreground">
          No other related domains found
        </div>
      )}

      {!isLoading && !error && filteredDomains.length > 0 && (
        <div className="space-y-2">
          {paginatedDomains.map((domain) => (
            <div
              key={domain.name}
              className="flex items-start justify-between gap-3 rounded-md border bg-background p-3 text-sm"
            >
              <div className="min-w-0 flex-1 space-y-1">
                <div className="font-medium">{domain.name}</div>
                {domain.organization_legal_name && (
                  <div className="text-xs text-muted-foreground">
                    {domain.organization_legal_name}
                  </div>
                )}
                {domain.licenses && (
                  <div className="flex flex-wrap gap-1">
                    {domain.licenses.split(',').map((license, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {license.trim()}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="size-3" />
                  {domain.last_verified
                    ? format(new Date(domain.last_verified), 'MM/dd/yy')
                    : 'Never'}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 text-xs"
                  disabled={!!dealLoading[domain.name]}
                  onClick={async () => handleViewRecentDeal(domain.name)}
                >
                  {dealLoading[domain.name]
                    ? <Loader2 className="size-3 animate-spin" />
                    : (
                        <>
                          View Deal
                          <ExternalLink className="size-3" />
                        </>
                      )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && filteredDomains.length > 0 && (
        <div className="space-y-2 text-xs text-muted-foreground">
          <div>
            Found
            {' '}
            {filteredDomains.length}
            {' '}
            other domain
            {filteredDomains.length !== 1 && 's'}
            {' '}
            with matching email domain or license
          </div>
          {filteredDomains.length > PAGE_SIZE && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                Showing
                {' '}
                {startIndex + 1}
                {' '}
                -
                {' '}
                {startIndex + paginatedDomains.length}
                {' '}
                of
                {' '}
                {filteredDomains.length}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
